import {
  Button,
  Flex,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DataTrack from '@Components/DataTrack';
import ConditionalTooltip from '@Components/Common/ConditionnalTooltip';
import PermissionPopover from '@Welcome/EmailAndCollaboration/components/PermissionPopover';
import { activateSolutionTrial } from '@Welcome/EmailAndCollaboration/utils/solutionTrial';
import { useSolutionTrialEligibility } from '@Welcome/EmailAndCollaboration/hooks/useSolutionTrialEligibility';
import {
  useUicPermissions,
  hasManageTrialsPermission,
} from '@Models/useUicPermissions';
import { useGeneralTrialStatus } from '@Welcome/EmailAndCollaboration/hooks/useGeneralTrialStatus';
import { IsAdvancedInfo } from '@Welcome/EmailAndCollaboration';
import common from '@Utils/common';
import { useGlobalPermissions } from '@Models/useGlobalPermissions';
// Test Preview (Development Only) - Uncomment to test
//import SolutionTrialContentTestPreview from '@Welcome/EmailAndCollaboration/components/SolutionTrialContent/TestPreview';

const EnableEmailAndCollaboration = ({
  provisionNewCas,
  actualProvisionNewCas,
  isCreditOneConsumptionFeatureShow,
}: {
  provisionNewCas: () => void;
  actualProvisionNewCas: () => Promise<void>;
  isCreditOneConsumptionFeatureShow: boolean;
}) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();

  // Check permissions (highest priority)
  const { data: permissionsData, isLoading: isPermissionsLoading } =
    useUicPermissions();
  const hasFreeTrialPermission = hasManageTrialsPermission(permissionsData);

  // Check trial eligibility
  const { data: eligibilityData, isLoading: isEligibilityLoading } =
    useSolutionTrialEligibility();
  const { data: isGeneralTrial, isLoading: isGeneralTrialLoading } =
    useGeneralTrialStatus();
  const { data: hasFullPermissions, isLoading: isGlobalPermissionsLoading } =
    useGlobalPermissions();
  const isEligible = eligibilityData?.isEligible ?? false;
  const trialStatus = eligibilityData?.latestTrial?.status;
  const isTrialActivated = trialStatus === 'Activated';
  const isTrialExpired =
    trialStatus && ['Expired', 'SkipToExpired'].includes(trialStatus);
  const uicAdvancedInfo: IsAdvancedInfo =
    common.safeParseLocalStorage<IsAdvancedInfo>('uic_advancedInfo', {
      isFoundation: false,
      isCredit: false,
      isMigrated: false,
      userType: '',
      isPaid: 0,
      validCredit: 0,
    });

  const trialButtonStatus = useMemo<{
    show: boolean;
    disabled?: boolean;
    disabledReason?: string;
    permissionType?: 'manageTrial' | 'fullPermission';
  }>(() => {
    if (
      isPermissionsLoading ||
      isEligibilityLoading ||
      isGeneralTrialLoading ||
      isGlobalPermissionsLoading
    ) {
      return { show: false };
    }
    if (uicAdvancedInfo.view !== 'ents' || isGeneralTrial === true) {
      return { show: false };
    }
    if (isEligible) {
      if (!hasFullPermissions) {
        return {
          show: true,
          disabled: true,
          disabledReason: t('welcome.error.noFullPermission'),
        };
      } else if (hasFreeTrialPermission) {
        return { show: true, disabled: false };
      } else {
        return {
          show: true,
          disabled: true,
          permissionType: 'manageTrial' as const,
        };
      }
    } else {
      if (isTrialActivated) {
        return { show: true, disabled: false };
      } else if (isTrialExpired) {
        return {
          show: true,
          disabled: true,
          disabledReason: t('welcome.solutionTrial.tooltip.freeTrialExpired'),
        };
      } else {
        return { show: false };
      }
    }
  }, [
    t,
    hasFreeTrialPermission,
    hasFullPermissions,
    isEligible,
    isGeneralTrial,
    isTrialExpired,
    isTrialActivated,
    uicAdvancedInfo.view,
    isPermissionsLoading,
    isEligibilityLoading,
    isGeneralTrialLoading,
    isGlobalPermissionsLoading,
  ]);

  const handleStartFreeTrial = async () => {
    // Scenario 1: User is eligible - activate trial
    if (isEligible) {
      try {
        await activateSolutionTrial(actualProvisionNewCas);
      } catch (error) {
        console.error('Failed to start free trial:', error);
        window.showNotification({
          titleMsg: t('common.apiException.internalError'),
          severity: 'error',
        });
      }
      return;
    }

    // Scenario 2: User is not eligible but has activated trial - provision directly
    // Skip ConfirmModal and call actualProvisionNewCas directly
    if (!isEligible && isTrialActivated) {
      await actualProvisionNewCas();
      return;
    }

    // Scenario 3: Button should be disabled (handled by isTrialButtonDisabled)
  };

  const freeTrialButton = (
    <Button
      variant="secondary"
      onClick={handleStartFreeTrial}
      disabled={trialButtonStatus.disabled || false}
      px="12px"
      py="6px"
      bg="transparent"
      color={colorStyle.color.secondary}
      border="1px solid"
      borderColor="#5e5e5e"
      borderRadius="3px"
      fontSize="14px"
      lineHeight="20px"
      _hover={{
        borderColor: '#8e8e8e',
      }}
    >
      <Text>{t('welcome.button.startFreeTrial')}</Text>
    </Button>
  );

  return (
    <Flex flexDirection="column" alignItems="center" width="100%" gap="24px">
      <Flex gap="12px" justifyContent="center">
        <DataTrack type={`ProvisionNew.Click`}>
          <ConditionalTooltip
            condition={
              !isGlobalPermissionsLoading && hasFullPermissions === false
            }
            label={t('welcome.error.noFullPermission')}
            placement="bottom"
            width="296px"
          >
            <Button
              variant="primary"
              onClick={provisionNewCas}
              disabled={
                isGlobalPermissionsLoading || hasFullPermissions === false
              }
              px="12px"
              py="6px"
              color={colorStyle.color.primary}
              borderRadius="3px"
              fontSize="14px"
              lineHeight="20px"
              border="none"
              _hover={{
                bg: 'linear-gradient(89.91deg, #9F51F9 0%, #679AFF 100%)',
              }}
              _active={{
                bg: 'linear-gradient(89.91deg, #8F41E9 0%, #578AEF 100%)',
              }}
              style={{
                background:
                  'linear-gradient(89.91deg, #8F41E9 0%, #578AEF 100%)',
                backgroundOrigin: 'border-box',
              }}
            >
              <Text>
                {uicAdvancedInfo.view !== 'ents' || isGeneralTrial === true
                  ? t('welcome.blockingPage.getStarted')
                  : t(
                      'welcome.enableCloudEmailAndCollaborationProtection.action.label'
                    )}
              </Text>
            </Button>
          </ConditionalTooltip>
        </DataTrack>
        {isCreditOneConsumptionFeatureShow &&
          trialButtonStatus.show &&
          (trialButtonStatus.permissionType ? (
            <PermissionPopover show={trialButtonStatus.disabled || false}>
              {freeTrialButton}
            </PermissionPopover>
          ) : (
            <ConditionalTooltip
              condition={trialButtonStatus.disabled || false}
              label={trialButtonStatus.disabledReason || ''}
              placement="bottom"
              width="296px"
            >
              {freeTrialButton}
            </ConditionalTooltip>
          ))}
      </Flex>

      {/* Test Preview (Development Only) - Uncomment to test */}
      {/* <SolutionTrialContentTestPreview />*/}
    </Flex>
  );
};
export default EnableEmailAndCollaboration;
